<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import dealStatusAPI from 'dashboard/api/pipeline/dealStatus';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const { showAlert } = useAlert();
const submitting = ref(false);

// Fase 3.1 — ofertas do card (lista pra o vendedor escolher qual ganhou)
const offers = computed(() => {
  const raw = props.item?.item_details?.offers;
  return Array.isArray(raw) ? raw : [];
});

const selectedOfferIndex = ref(null);
const wonValue = ref('');
const wonNote = ref('');

// Pré-seleciona a oferta marcada como padrão (Fase 3.2) ou a única existente
watch(
  () => props.show,
  visible => {
    if (!visible) {
      selectedOfferIndex.value = null;
      wonValue.value = '';
      wonNote.value = '';
      return;
    }
    const list = offers.value;
    if (!list.length) return;
    const defaultIdx = list.findIndex(o => o?.is_default === true);
    let idx = null;
    if (defaultIdx >= 0) {
      idx = defaultIdx;
    } else if (list.length === 1) {
      idx = 0;
    }
    if (idx !== null) {
      selectedOfferIndex.value = idx;
      wonValue.value = list[idx]?.value ?? '';
    }
  },
  { immediate: true }
);

function selectOffer(idx) {
  selectedOfferIndex.value = idx;
  const offer = offers.value[idx];
  if (offer && offer.value !== undefined && offer.value !== null) {
    wonValue.value = offer.value;
  }
}

function formatOfferValue(offer) {
  if (!offer || offer.value === undefined || offer.value === null) return '';
  const symbol =
    (offer.currency && typeof offer.currency === 'object'
      ? offer.currency.symbol
      : offer.currency) || 'R$';
  const num = Number(offer.value);
  if (Number.isNaN(num)) return `${symbol} ${offer.value}`;
  return `${symbol} ${num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Normalizes BR decimal input (1.234,56) to API-friendly (1234.56).
// Leaves the empty string as-is so the API treats it as "no value".
function normalizeDecimalInput(raw) {
  if (raw === null || raw === undefined) return '';
  const str = String(raw).trim();
  if (str === '') return '';
  // Remove thousand separators, convert decimal comma to dot.
  return str.replace(/\./g, '').replace(',', '.');
}

async function handleConfirm() {
  if (submitting.value) return;

  submitting.value = true;
  try {
    // The endpoint returns the authoritative deal-status payload
    // ({ id, status, pipeline_stage, won_at, won_value, ... }). Forward it on
    // `success` so the parent can patch the open card AND the board store
    // synchronously — no stale window waiting on a follow-up GET.
    const { data } = await dealStatusAPI.markAsWon(props.item.id, {
      wonValue: normalizeDecimalInput(wonValue.value),
      wonNote: wonNote.value,
      winningOfferIndex: selectedOfferIndex.value,
    });

    showAlert(t('PIPELINE.DEAL_STATUS.SUCCESS.MARKED_WON'));
    emit('success', data);
    emit('close');
  } catch (error) {
    showAlert(t('PIPELINE.DEAL_STATUS.ERROR.MARK_FAILED'), 'error');
  } finally {
    submitting.value = false;
  }
}

function handleCancel() {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <!-- z-[9999]: must sit ABOVE the generic Modal wrapper (z-[9990]) used by the
         agenda / PipelineTab to host PipelineCardDetails. With z-50 this popup
         rendered BEHIND that modal and was invisible (won/lost never confirmed). -->
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 dark:bg-black/70"
      @click.self="handleCancel"
    >
      <div class="w-full max-w-md rounded-2xl bg-n-solid-2 p-6 shadow-lg">
        <!-- Header -->
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.TITLE') }}
          </h3>
          <button
            class="rounded-lg p-1 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            @click="handleCancel"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>

        <!-- Message -->
        <p class="mb-4 text-body-main text-n-slate-11">
          {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.MESSAGE') }}
        </p>

        <!-- Item Info -->
        <div class="mb-4 rounded-lg bg-n-teal-3 border border-n-teal-9 p-4">
          <div class="text-label font-medium text-n-teal-11">
            {{ item.item_details?.title || item.title || 'Untitled' }}
          </div>
        </div>

        <!-- Fase 3.1 — Offer selection (only when card has offers) -->
        <div v-if="offers.length > 0" class="mb-4">
          <label class="block text-label font-medium text-n-slate-12 mb-2">
            {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.SELECT_OFFER') }}
          </label>
          <div class="space-y-2 max-h-48 overflow-y-auto">
            <label
              v-for="(offer, index) in offers"
              :key="index"
              class="flex items-start gap-3 rounded-lg border border-n-weak p-3 cursor-pointer hover:bg-n-alpha-1"
              :class="{
                'border-n-teal-9 bg-n-teal-3': selectedOfferIndex === index,
              }"
            >
              <input
                :checked="selectedOfferIndex === index"
                type="radio"
                name="winning_offer"
                class="mt-1"
                @change="selectOffer(index)"
              />
              <div class="flex-1 min-w-0">
                <div class="text-label text-n-slate-12">
                  {{
                    offer.description ||
                    $t('PIPELINE.DEAL_STATUS.WON_MODAL.OFFER_UNTITLED', {
                      index: index + 1,
                    })
                  }}
                </div>
                <div class="text-label-small font-semibold text-n-teal-11 mt-1">
                  {{ formatOfferValue(offer) }}
                </div>
                <span
                  v-if="offer.is_default"
                  class="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
                >
                  {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.DEFAULT_OFFER') }}
                </span>
              </div>
            </label>
          </div>
        </div>

        <!-- Won value (manual override / no-offers fallback) -->
        <!-- BUG-008 fix: removida classe `field-base` redundante (o seletor
             global em _base.scss já aplica a inputs type=number). Trocado
             `type=number` por `inputmode=decimal` para evitar spinner
             buttons nativos do Chrome conflitando com o layout do modal. -->
        <div class="mb-4">
          <label
            for="mark-won-value-input"
            class="block text-label font-medium text-n-slate-12 mb-1"
          >
            {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_VALUE_LABEL') }}
          </label>
          <input
            id="mark-won-value-input"
            v-model="wonValue"
            type="text"
            inputmode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            class="no-margin h-10 w-full"
            :placeholder="
              $t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_VALUE_PLACEHOLDER')
            "
          />
        </div>

        <!-- Won note (optional) -->
        <div class="mb-6">
          <label
            for="mark-won-note-textarea"
            class="block text-label font-medium text-n-slate-12 mb-1"
          >
            {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_LABEL') }}
          </label>
          <textarea
            id="mark-won-note-textarea"
            v-model="wonNote"
            rows="2"
            class="field-base resize-none"
            :placeholder="
              $t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_PLACEHOLDER')
            "
          />
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <button
            class="flex-1 rounded-lg border border-n-weak px-4 py-2 text-label font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-50"
            :disabled="submitting"
            @click="handleCancel"
          >
            {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.CANCEL') }}
          </button>
          <button
            class="flex-1 rounded-lg bg-n-teal-9 px-4 py-2 text-label font-medium text-white hover:brightness-110 disabled:opacity-50"
            :disabled="submitting"
            @click="handleConfirm"
          >
            <span v-if="!submitting">
              {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.CONFIRM') }}
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <div
                class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              />
              {{ $t('PIPELINE.DEAL_STATUS.WON_MODAL.PROCESSING') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
